import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useJsonLd, breadcrumbSchema, specialtyCourseSchema, faqSchema } from "@/hooks/useJsonLd";
import { ArrowLeft, ArrowRight, BookOpen, Award, Clock, Lock, GraduationCap, CheckCircle2 } from "lucide-react";

/**
 * Public-facing track syllabus page. Crawlable by Google, viewable by anyone.
 * Shows module + lesson titles, hours, and final exam meta — but NOT lesson body.
 * Lesson body remains gated behind /lesson/:id (paid).
 *
 * Each track gets its own OG image at /og/track-[id].png.
 */

// Hand-curated "you might also like" pairings — pulls students toward the
// natural next cert in their career arc.
const RELATED_TRACKS = {
  "fdny-s95":            ["fdny-f53", "fdny-s12"],
  "fdny-f53":            ["smoke-voice", "fdny-s95"],
  "fdny-s12":            ["fdny-s13", "fdny-s95"],
  "fdny-s13":            ["fdny-s12", "fdny-s95"],
  "bms-elevator":        ["smoke-voice", "manufacturer-exposure"],
  "smoke-voice":         ["fdny-f53", "bms-elevator"],
  "manufacturer-exposure": ["bms-elevator", "fdny-s95"],
};
const TRACK_LABELS = {
  "fdny-s95": "FDNY S-95 · Visual Inspection",
  "fdny-f53": "FDNY F-53 · One-Way Voice",
  "fdny-s12": "FDNY S-12 · Citywide Sprinkler",
  "fdny-s13": "FDNY S-13 · Citywide Standpipe",
  "bms-elevator": "BMS + Elevator Recall",
  "smoke-voice": "Smoke Control + Voice EVACS",
  "manufacturer-exposure": "Manufacturer Exposure (EST3/Siemens/Notifier)",
};

export default function TrackPublic() {
  const { trackId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/public/tracks/${trackId}`);
        setData(d);
      } catch {
        // Public track fetch failed; the !data render branch handles UX
      } finally {
        setLoading(false);
      }
    })();
  }, [trackId]);

  // Hooks must run unconditionally — gate the data via 'data?' guards inside
  useDocumentMeta({
    title: data ? `${data.name} — ${data.hours}-hour track` : "Specialty Track — APM Fire Alarm Exam Prep",
    description: data
      ? `${data.description.slice(0, 155)}`
      : "Specialty fire alarm training track at APM Fire Alarm Exam Prep.",
    image: data ? `/og/track-${data.id}.png` : "/og/og-home.png",
    path: `/track/${trackId}`,
  });
  useJsonLd(
    data
      ? [
          specialtyCourseSchema(data),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Specialty Tracks", url: "/tracks" },
            { name: data.short_name, url: `/track/${data.id}` },
          ]),
          faqSchema([
            {
              q: `Who is the ${data.short_name} track for?`,
              a: `This track is designed for fire alarm technicians, electricians, and supervisors pursuing the ${data.credential}. Best suited for students with existing fire-alarm fundamentals.`,
            },
            {
              q: `How long is the ${data.short_name} track?`,
              a: `${data.hours} hours of instruction broken across ${data.modules?.length || 0} modules. Self-paced — most students complete it in 2-4 weeks.`,
            },
            {
              q: `Do I get a credential after passing the final exam?`,
              a: `Yes. Passing the ${data.short_name} final exam (70%+) earns the "${data.credential}" credential, displayed on your APM transcript and downloadable via PDF.`,
            },
            {
              q: `Is this track included in the $149 enrollment?`,
              a: `Yes. All 7 specialty tracks are included free with $149 lifetime enrollment in APM Fire Alarm Exam Prep.`,
            },
          ]),
        ]
      : null
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="max-w-5xl mx-auto px-6 py-20 text-zinc-500 font-mono text-sm text-center">Loading...</main>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="max-w-5xl mx-auto px-6 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold tracking-tight mb-2">Track not found</h1>
          <Link to="/" className="text-red-600 hover:underline">← Back to home</Link>
        </main>
      </div>
    );
  }

  const totalLessons = data.modules.reduce((a, m) => a + m.lessons.length, 0);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main data-testid={`track-public-${trackId}`}>
        {/* Hero */}
        <section className="bg-zinc-950 text-white">
          <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
            <Link to="/" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1 mb-6">
              <ArrowLeft className="w-4 h-4" /> Home
            </Link>
            <div className="overline text-zinc-400 mb-3"><span className="red-bar" />Specialty track</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95]" data-testid="track-headline">
              {data.name}
            </h1>
            <p className="text-zinc-300 mt-6 max-w-2xl text-lg leading-relaxed">{data.description}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-10 max-w-3xl">
              <div className="border-l-2 pl-3" style={{ borderColor: data.color }}>
                <div className="font-heading text-3xl font-bold tracking-tight">{data.hours}</div>
                <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-400 mt-1">Hours</div>
              </div>
              <div className="border-l-2 border-zinc-700 pl-3">
                <div className="font-heading text-3xl font-bold tracking-tight">{data.modules.length}</div>
                <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-400 mt-1">Modules</div>
              </div>
              <div className="border-l-2 border-zinc-700 pl-3">
                <div className="font-heading text-3xl font-bold tracking-tight">{totalLessons}</div>
                <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-400 mt-1">Lessons</div>
              </div>
              <div className="border-l-2 border-zinc-700 pl-3">
                <div className="font-heading text-3xl font-bold tracking-tight">
                  {data.final_exam?.total_questions || 0}
                </div>
                <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-400 mt-1">Final exam Qs</div>
              </div>
            </div>
            <div className="mt-10 flex gap-3 flex-wrap">
              <Link to="/enroll">
                <button className="btn-primary" data-testid="track-public-enroll">
                  Enroll — $149 lifetime <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/free-guide">
                <button className="btn-outline-light" data-testid="track-public-free-guide">
                  <BookOpen className="w-4 h-4" /> Try 25 free questions
                </button>
              </Link>
            </div>
          </div>
        </section>

        {/* Credential */}
        <section className="border-b border-zinc-200 py-12" data-testid="track-credential">
          <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-wrap items-center gap-4">
            <div className="w-12 h-12 grid place-items-center text-white flex-shrink-0" style={{ backgroundColor: data.color }}>
              <Award className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="overline mb-1">Credential earned on completion</div>
              <div className="font-heading text-xl font-bold tracking-tight">{data.credential}</div>
              <div className="text-xs font-mono text-zinc-500 mt-0.5">
                Pass the final exam ({data.final_exam?.passing_score_pct || 70}%+) to earn this credential
              </div>
            </div>
          </div>
        </section>

        {/* Module + lesson syllabus */}
        <section className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16" data-testid="track-syllabus">
          <div className="overline mb-3"><span className="red-bar" />Full syllabus</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-3">
            What you'll learn
          </h2>
          <p className="text-sm text-zinc-600 mb-10 max-w-2xl">
            {data.modules.length} modules · {totalLessons} lessons · {data.hours} total hours · final exam
          </p>
          <div className="space-y-6">
            {data.modules.map((m, mIdx) => (
              <div key={m.id} className="card-tech p-6" data-testid={`syllabus-module-${m.id}`}>
                <div className="flex items-baseline justify-between gap-3 mb-4">
                  <h3 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">
                    {m.title}
                  </h3>
                  <div className="text-xs font-mono text-zinc-500 flex items-center gap-1 flex-shrink-0">
                    <Clock className="w-3.5 h-3.5" /> {m.hours} hrs
                  </div>
                </div>
                <ul className="space-y-2">
                  {m.lessons.map((les, lIdx) => (
                    <li key={les.id} className="flex items-start gap-3 text-sm" data-testid={`syllabus-lesson-${les.id}`}>
                      <span className="font-mono text-[10px] text-zinc-400 mt-1 w-12 flex-shrink-0">
                        L{lIdx + 1}.{(lIdx + 1).toString().padStart(2, "0")}
                      </span>
                      <span className="text-zinc-800 flex-1">{les.title}</span>
                      <span className="text-xs font-mono text-zinc-500 flex-shrink-0">{les.hours} hr</span>
                    </li>
                  ))}
                  {m.quizzes_count > 0 && (
                    <li className="flex items-start gap-3 text-sm pt-2 mt-2 border-t border-zinc-100">
                      <span className="font-mono text-[10px] text-zinc-400 mt-1 w-12 flex-shrink-0">QUIZ</span>
                      <span className="text-zinc-600 italic flex-1">Module assessment · {m.quizzes_count} quiz{m.quizzes_count > 1 ? "zes" : ""}</span>
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>

          {/* Final exam */}
          {data.final_exam && (
            <div className="card-tech p-6 mt-6 border-red-200 bg-red-50/40" data-testid="syllabus-final">
              <div className="flex items-center gap-3 mb-2">
                <GraduationCap className="w-6 h-6 text-red-600" />
                <h3 className="font-heading text-xl font-bold tracking-tight">{data.final_exam.title}</h3>
              </div>
              <div className="text-sm text-zinc-700 flex flex-wrap items-center gap-x-6 gap-y-1">
                <span><strong>{data.final_exam.total_questions}</strong> questions</span>
                <span><strong>{data.final_exam.passing_score_pct}%</strong> to pass</span>
                {data.final_exam.time_limit_minutes && <span><strong>{data.final_exam.time_limit_minutes}</strong> min suggested</span>}
                <span>Earns: <strong>{data.credential}</strong></span>
              </div>
            </div>
          )}
        </section>

        {/* Lock callout */}
        <section className="border-y border-zinc-200 bg-zinc-50 py-10" data-testid="track-lock-callout">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <Lock className="w-8 h-8 text-zinc-400 mx-auto mb-3" />
            <h3 className="font-heading text-2xl font-bold tracking-tight mb-2">Lesson content unlocks with enrollment</h3>
            <p className="text-sm text-zinc-600 mb-5 max-w-md mx-auto">
              You're viewing the public syllabus. Full lesson body, interactive quizzes, AI tutor, and final exam unlock with $149 lifetime enrollment.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-red-600" />
              <span>All 7 specialty tracks included</span>
              <span className="text-zinc-300">·</span>
              <CheckCircle2 className="w-4 h-4 text-red-600" />
              <span>Lifetime access</span>
              <span className="text-zinc-300">·</span>
              <CheckCircle2 className="w-4 h-4 text-red-600" />
              <span>No subscription</span>
            </div>
          </div>
        </section>

        {/* Related tracks — internal linking for SEO + student discovery */}
        <section className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16" data-testid="related-tracks">
          <div className="overline mb-3"><span className="red-bar" />Continue your path</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-8">
            Related specialty tracks
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {(RELATED_TRACKS[data.id] || []).map((relId) => (
              <Link
                key={relId}
                to={`/track/${relId}`}
                className="card-tech p-5 hover:shadow-md hover:border-red-300 transition-all group"
                data-testid={`related-track-${relId}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="overline mb-1.5">Specialty track</div>
                    <div className="font-heading text-lg font-bold tracking-tight group-hover:text-red-600 transition-colors">
                      {TRACK_LABELS[relId] || relId}
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-zinc-400 group-hover:text-red-600 group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                </div>
              </Link>
            ))}
            <Link to="/career" className="card-tech p-5 hover:shadow-md hover:border-red-300 transition-all group" data-testid="related-career">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="overline mb-1.5">See the full picture</div>
                  <div className="font-heading text-lg font-bold tracking-tight group-hover:text-red-600 transition-colors">
                    Career Roadmap + Salary Guide
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-400 group-hover:text-red-600 group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
              </div>
            </Link>
            <Link to="/free-guide" className="card-tech p-5 hover:shadow-md hover:border-red-300 transition-all group" data-testid="related-free-guide">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="overline mb-1.5">Try before you buy</div>
                  <div className="font-heading text-lg font-bold tracking-tight group-hover:text-red-600 transition-colors">
                    25 free S-98 practice questions
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-400 group-hover:text-red-600 group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
              </div>
            </Link>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="bg-zinc-950 text-white py-20" data-testid="track-bottom-cta">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter mb-4">
              Ready to earn your {data.short_name}?
            </h2>
            <p className="text-zinc-300 mb-8 max-w-2xl mx-auto text-sm">
              Plus 6 other specialty tracks, 140-hr NICET curriculum, AI tutor, interactive labs, and a QR-verified certificate — all in one $149 enrollment.
            </p>
            <Link to="/enroll">
              <button className="btn-primary" data-testid="track-bottom-enroll">
                Enroll for $149 <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
